// Story 4.2 — TanStack Query hook wrapping GET /api/v1/vault/items.
//
// The vault grid's single source of truth. Cache key includes every
// filter+sort+pagination arg so each combination is a separate cache
// entry; TanStack handles invalidation naturally.
//
// Invalidated by:
//   * useReveal.onSuccess (Story 3.6 hook EXTENDED in Story 4.2) so a
//     successful reveal flips the just-opened item from sealed →
//     opened in the vault grid without a manual page refresh.
//
// Mirrors Story 3.9 useWelcomeDossier.ts shape + Story 1.27
// useAccountMe.ts shape.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type {
  VaultItemsQueryParams,
  VaultItemsResponse,
} from "./vaultTypes";

const DEFAULT_PER_PAGE = 24;

export interface VaultItemsFetchError {
  code: string;
  message: string;
  status: number;
}

function buildQueryString(params: VaultItemsQueryParams): string {
  const search = new URLSearchParams();
  search.set("page", String(params.page ?? 1));
  search.set("per_page", String(params.per_page ?? DEFAULT_PER_PAGE));
  if (params.filter) search.set("filter", params.filter);
  if (params.tier) search.set("tier", params.tier);
  if (params.drop_id) search.set("drop_id", params.drop_id);
  if (params.sort) search.set("sort", params.sort);
  return search.toString();
}

async function fetchVaultItems(
  params: VaultItemsQueryParams,
): Promise<VaultItemsResponse> {
  const qs = buildQueryString(params);
  const response = await fetch(`/api/v1/vault/items?${qs}`, {
    credentials: "include",
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: VaultItemsFetchError = {
      code: "VAULT_ITEMS_FETCH_FAILED",
      message: `Vault items fetch failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as VaultItemsResponse;
}

/// Build a stable, JSON-serializable cache key for the given vault
/// query. Object-shape ensures TanStack treats different filter/sort
/// combinations as distinct entries.
export function vaultItemsQueryKey(params: VaultItemsQueryParams) {
  return [
    "vault-items",
    {
      page: params.page ?? 1,
      per_page: params.per_page ?? DEFAULT_PER_PAGE,
      filter: params.filter ?? "all",
      tier: params.tier ?? "all",
      drop_id: params.drop_id ?? null,
      sort: params.sort ?? "acquired_at_desc",
    },
  ] as const;
}

export function useVaultItems(params: VaultItemsQueryParams = {}) {
  return useQuery<VaultItemsResponse, VaultItemsFetchError>({
    queryKey: vaultItemsQueryKey(params),
    queryFn: () => fetchVaultItems(params),
    staleTime: 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}
